
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface PayloadRendererProps {
  payload: any;
  compact?: boolean;
}

export const PayloadRenderer = ({ payload, compact = false }: PayloadRendererProps) => {
  const handleCopy = async (value: any) => {
    try {
      await navigator.clipboard.writeText(typeof value === 'string' ? value : JSON.stringify(value, null, 2));
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const renderValue = (key: string, value: any, index: number) => {
    // Handle different value types
    if (value === null || value === undefined) {
      return (
        <div key={index} className="flex items-center justify-between py-1.5 border-b border-border/20 last:border-0">
          <span className="text-xs font-medium text-muted-foreground truncate">{key}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">null</Badge>
        </div>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <div key={index} className="flex items-center justify-between py-1.5 border-b border-border/20 last:border-0">
          <span className="text-xs font-medium text-muted-foreground truncate">{key}</span>
          <Badge variant={value ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">
            {value ? 'true' : 'false'}
          </Badge>
        </div>
      );
    }

    if (typeof value === 'number') {
      return (
        <div key={index} className="flex items-center justify-between py-1.5 border-b border-border/20 last:border-0">
          <span className="text-xs font-medium text-muted-foreground truncate">{key}</span>
          <div className="flex items-center gap-1.5">
            <code className="text-xs px-1.5 py-0.5 bg-muted/60 rounded font-mono text-foreground/90">
              {value.toLocaleString()}
            </code>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 hover:bg-background/80"
              onClick={() => handleCopy(value)}
            >
              <Copy className="h-2.5 w-2.5" />
            </Button>
          </div>
        </div>
      );
    }

    if (typeof value === 'string') {
      const isUrl = /^https?:\/\/.+/.test(value);
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      const maxLength = compact ? 25 : 35;
      const truncatedValue = value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;
      
      return (
        <div key={index} className="flex items-center justify-between py-1.5 border-b border-border/20 last:border-0">
          <span className="text-xs font-medium text-muted-foreground truncate max-w-[80px]">{key}</span>
          <div className="flex items-center gap-1.5 max-w-[180px]">
            {isUrl ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-2 text-[10px] text-primary hover:text-primary/80 hover:bg-primary/10"
                onClick={() => window.open(value, '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink className="h-2.5 w-2.5 mr-1" />
                Link
              </Button>
            ) : (
              <span className={cn(
                "text-xs truncate font-mono",
                isEmail ? "text-primary" : "text-foreground/80"
              )} title={value}>
                {truncatedValue}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 hover:bg-background/80"
              onClick={() => handleCopy(value)}
            >
              <Copy className="h-2.5 w-2.5" />
            </Button>
          </div>
        </div>
      );
    }

    if (Array.isArray(value)) {
      const itemsToShow = compact ? 2 : 3;
      return (
        <div key={index} className="py-1.5 border-b border-border/20 last:border-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-muted-foreground truncate">{key}</span>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {value.length} items
            </Badge>
          </div>
          <div className="space-y-1 pl-2 border-l-2 border-border/40">
            {value.slice(0, itemsToShow).map((item, idx) => (
              <div key={idx} className="text-xs text-muted-foreground/80 font-mono truncate">
                <span className="text-muted-foreground/60">{idx}:</span> {
                  typeof item === 'object' && item !== null 
                    ? `{${Object.keys(item).slice(0, 2).join(', ')}${Object.keys(item).length > 2 ? '...' : '}'}` 
                    : String(item).length > 30 
                      ? `${String(item).substring(0, 30)}...`
                      : String(item)
                }
              </div>
            ))}
            {value.length > itemsToShow && (
              <div className="text-xs text-muted-foreground/60 italic">
                ... {value.length - itemsToShow} more items
              </div>
            )}
          </div>
        </div>
      );
    }

    if (typeof value === 'object' && value !== null) {
      const keysToShow = compact ? 2 : 3;
      const objectKeys = Object.keys(value);
      
      return (
        <div key={index} className="py-1.5 border-b border-border/20 last:border-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-muted-foreground truncate">{key}</span>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {objectKeys.length} fields
            </Badge>
          </div>
          <div className="space-y-1 pl-2 border-l-2 border-border/40">
            {objectKeys.slice(0, keysToShow).map((k, idx) => (
              <div key={idx} className="flex justify-between text-xs gap-2">
                <span className="text-muted-foreground/70 font-medium truncate max-w-[60px]">{k}:</span>
                <span className="text-foreground/80 font-mono max-w-[100px] truncate" title={String(value[k])}>
                  {typeof value[k] === 'object' && value[k] !== null
                    ? '[object]' 
                    : String(value[k]).length > 20
                      ? `${String(value[k]).substring(0, 20)}...`
                      : String(value[k])
                  }
                </span>
              </div>
            ))}
            {objectKeys.length > keysToShow && (
              <div className="text-xs text-muted-foreground/60 italic">
                ... {objectKeys.length - keysToShow} more fields
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={index} className="flex items-center justify-between py-1.5 border-b border-border/20 last:border-0">
        <span className="text-xs font-medium text-muted-foreground truncate">{key}</span>
        <span className="text-xs text-foreground/80 font-mono truncate" title={String(value)}>
          {String(value)}
        </span>
      </div>
    );
  };

  if (!payload || (typeof payload === 'object' && payload !== null && Object.keys(payload).length === 0)) {
    return (
      <div className="text-center py-3">
        <Badge variant="outline" className="text-[10px] px-2 py-1">Empty Response</Badge>
      </div>
    );
  }

  if (typeof payload !== 'object' || payload === null) {
    return (
      <div className="flex items-center justify-between py-2">
        <span className="text-xs font-medium text-muted-foreground">Response</span>
        <div className="flex items-center gap-1.5">
          <code className="text-xs px-2 py-1 bg-muted/60 rounded font-mono text-foreground/90">
            {String(payload)}
          </code>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 hover:bg-background/80"
            onClick={() => handleCopy(payload)}
          >
            <Copy className="h-2.5 w-2.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {Object.entries(payload).map(([key, value], index) => renderValue(key, value, index))}
    </div>
  );
};
